import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Users } from 'lucide-react';
import { ExternalUsersListPage as SharedExternalUsersListPage } from '@/features/external-users/components/ExternalUsersListPage';
import { useConfirmDialog } from '../../../contexts/ConfirmDialogContext';
import { useToggleUserStatus } from '../../../hooks/users/useExternalUsers';
import { Can } from '../../../components/Can';
import { usePermissions } from '../../../hooks/auth/usePermissions';

interface ExternalUserListItem {
  id: string;
  firstname: string;
  lastname: string;
  isActive: boolean;
}

export const ExternalUsersListPage = () => {
  const navigate = useNavigate();
  const { confirm } = useConfirmDialog();
  const { can } = usePermissions();
  const toggleStatus = useToggleUserStatus();
  const [total, setTotal] = useState<number | undefined>();

  const pluralize = (count: number) => count > 1 ? 'éléments' : 'élément';

  const handleToggleStatus = async (user: ExternalUserListItem) => {
    const confirmed = await confirm({
      title: user.isActive ? 'Désactiver l\'utilisateur' : 'Activer l\'utilisateur',
      message: user.isActive
        ? `Êtes-vous sûr de vouloir désactiver ${user.firstname} ${user.lastname} ? L'utilisateur ne pourra plus se connecter.`
        : `Êtes-vous sûr de vouloir activer ${user.firstname} ${user.lastname} ? L'utilisateur pourra se connecter.`,
      confirmLabel: user.isActive ? 'Désactiver' : 'Activer',
      variant: user.isActive ? 'danger' : 'info',
    });

    if (!confirmed) return;
    await toggleStatus.mutateAsync(user.id);
  };

  return (
    <div>
      {/* Header */}
      <div className="tw-pt-4">
        <div className="tw-flex tw-items-center tw-justify-between">
          <div className="tw-flex tw-items-center tw-gap-3">
            <Users className="tw-w-8 tw-h-8 tw-text-affinicar-primary" />
            <h1 className="tw-text-2xl tw-font-bold tw-text-gray-900 tw-mb-0">
              Utilisateurs
              {total !== undefined && (
                <span className="tw-ml-1 tw-text-base tw-font-normal tw-text-gray-400">
                  ({total} {pluralize(total)})
                </span>
              )}
            </h1>
          </div>

          <Can permission="external_user.create">
            <button
              data-testid="btn-new-external-user"
              onClick={() => navigate('/users-externes/new')}
              className="tw-px-4 tw-py-2 tw-bg-affinicar-primary tw-text-white tw-rounded-lg hover:tw-bg-affinicar-secondary tw-transition tw-font-medium"
            >
              + Nouvel utilisateur
            </button>
          </Can>
        </div>
      </div>

      <SharedExternalUsersListPage
        title="Utilisateurs"
        onRowClick={can('external_user.view') ? (user) => navigate(`/users-externes/${user.id}`) : undefined}
        onToggleStatus={can('external_user.delete') ? handleToggleStatus : undefined}
        canToggleStatus={can('external_user.delete')}
        hideTitle
        embedded
        onTotalChange={setTotal}
      />
    </div>
  );
};
